/**
 * search-modal-focus.js
 *
 * Accessibility fix for the header "Search our site" drawer
 * (#t4s-search-hidden).
 * (Search link — Ski Barn accessibility issue #49, SBMR-71)
 *
 * Clicking the search icon opens the drawer (aria-hidden flips to
 * "false") but never moves keyboard focus into it. Tab simply continues
 * through the rest of the page in normal document order — the drawer's
 * own controls (close button, category select, search input, results,
 * "view all" link) sit near the end of the DOM and are never reached from
 * the trigger. A keyboard user who opens the drawer keeps tabbing through
 * a page they can no longer see (it's covered by the drawer + overlay,
 * and clicks on it are blocked), with no indication of how to get back to
 * where they were — effectively a keyboard trap, even though Tab itself
 * never stops moving.
 *
 * The theme's own JS already closes any open `.t4s-drawer` on Escape
 * (see the `keyup.drawer` handler in global.min.js) and on clicking the
 * overlay/close button, all of which flip `aria-hidden` back to "true" —
 * but none of those paths return focus to whatever opened the drawer.
 *
 * This file only adds behavior on top of that: when the drawer opens, it
 * moves focus inside and traps Tab/Shift+Tab there; whenever the drawer
 * closes (by any of the existing methods above), it returns focus to the
 * element that opened it. It doesn't touch the theme's own open/close
 * logic, so it can't break how the drawer currently opens or closes.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // visibility:hidden keeps an element's box, so geometry alone calls it
  // visible while the browser still refuses focus() -- check styles too.
  function isVisible(el) {
    if (!el) return false;
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    return true;
  }

  function getFocusableElements(container) {
    var nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
    var focusable = [];
    for (var i = 0; i < nodes.length; i++) {
      if (isVisible(nodes[i])) focusable.push(nodes[i]);
    }
    return focusable;
  }

  function createFocusTrap(isOpen) {
    var activeDrawer = null;
    var triggerElement = null;
    var keydownHandler = null;

    function trapTabKey(event) {
      if (event.key !== 'Tab' || !activeDrawer) return;

      // Fail open: only block Tab once focus is genuinely inside, or a
      // refused focus() leaves the user with a dead Tab key.
      var focusable = getFocusableElements(activeDrawer);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (!activeDrawer.contains(document.activeElement)) {
        first.focus();
        if (activeDrawer.contains(document.activeElement)) event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function open(drawer) {
      if (activeDrawer === drawer) return;
      activeDrawer = drawer;
      triggerElement = document.activeElement;

      if (!drawer.hasAttribute('tabindex')) {
        drawer.setAttribute('tabindex', '-1');
      }

      // The drawer's open transition (visibility/transform) can still be
      // mid-flight for a bit after aria-hidden flips, and a browser won't
      // move focus onto an element that isn't actually visible/painted
      // yet. Rather than guess a fixed delay, retry the focus() call
      // itself until it actually takes (or give up after ~3s).
      var attemptsLeft = 60;
      (function attemptFocus() {
        if (activeDrawer !== drawer) return;
        var focusable = getFocusableElements(drawer);
        var target = focusable[0] || drawer;
        target.focus();
        if (document.activeElement === target || attemptsLeft <= 0) return;
        attemptsLeft--;
        window.setTimeout(attemptFocus, 50);
      })();

      keydownHandler = trapTabKey;
      document.addEventListener('keydown', keydownHandler, true);
    }

    function close() {
      if (!activeDrawer) return;

      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
      }

      if (triggerElement && typeof triggerElement.focus === 'function' && isVisible(triggerElement)) {
        triggerElement.focus();
      }

      activeDrawer = null;
      triggerElement = null;
    }

    return {
      sync: function (drawer) {
        if (isOpen(drawer)) {
          open(drawer);
        } else if (activeDrawer === drawer) {
          close();
        }
      }
    };
  }

  function init() {
    var drawer = document.getElementById('t4s-search-hidden');
    if (!drawer) return;

    var trap = createFocusTrap(function (el) {
      return el.getAttribute('aria-hidden') === 'false';
    });

    var observer = new MutationObserver(function () {
      trap.sync(drawer);
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });

    // In case the drawer is already open when this script runs.
    trap.sync(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
